import { type AuthPostMessage, promptGoogleAuth, promptOffice365Auth } from "~/lib/auth";
import { dispatchAnalyticsTrack, getCookiesOfInterest, getReferringEntities } from "~/lib/segment";
import toast from "~/ui/toast";

export const publicRecapSignup = async (
	provider: "google" | "office365",
	accessKey?: string,
): Promise<Pick<AuthPostMessage, "code" | "message" | "email" | "signed_email"> | void> => {
	const { provider: initialProvider } = window.INITIAL_STATE;

	/* eslint-disable camelcase */
	const analyticsData = {
		feature: "Authentication",
		integration: provider,
		integration_initial_provider: initialProvider,
		variant: "auth_prompt",
		...getReferringEntities(),
		...getCookiesOfInterest(),
	};
	/* eslint-enable camelcase */

	const authPrompt = provider === "google" ? promptGoogleAuth : promptOffice365Auth;

	try {
		const data = await authPrompt({
			recapKey: accessKey,
			fromPublicRecap: true,
			signup: "generic",
		});

		window.setTimeout(() => {
			if (data.signup?.redirect) {
				dispatchAnalyticsTrack("[Public Recap Signup] Redirecting existing user", {
					is_account_created: false, // eslint-disable-line camelcase
					redirect: data.signup.redirect,
					...analyticsData,
				});
				window.location.href = data.signup.redirect;
			} else if (data.signup?.next) {
				dispatchAnalyticsTrack("[Public Recap Signup] User action required, redirecting to generic signup", {
					is_account_created: false, // eslint-disable-line camelcase
					next: data.signup.next,
					...analyticsData,
				});
				// We should only get here if the user needs to view the signup join or personal signup page.
				// Since we only show the sign up button for emails who aren't associated with a user, this shouldn't happen very often.
				// Ideally we would redirect to the signup join or personal signup page, but that requires more effort than it's worth
				// for this experiment, so we just redirect to the generic signup page.
				window.location.href = `${window.ROOT_URL}${data.signup.next}`;
			} else if (data.next) {
				dispatchAnalyticsTrack("[Public Recap Signup] Signed Up", {
					is_account_created: true, // eslint-disable-line camelcase
					...analyticsData,
				});
				window.location.href = data.next;
			}
		}, 100);
	} catch (err) {
		/* eslint-disable camelcase */
		const { code, message, email, signed_email } = err as AuthPostMessage;
		dispatchAnalyticsTrack("[Public Recap Signup] Signup Failed", {
			reason: "integration_permission_rejected",
			code,
			message,
			email,
			signed_email,
			...analyticsData,
		});
		toast.error(message);
		return { code, message, email, signed_email };
		/* eslint-enable camelcase */
	}
};
